import { CalendarDays } from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { useMobilisationsList } from '../../hooks/useMobilisations';
import { formatDate } from '../../lib/dateFmt';

const NO_MOBILISATION = '__none__';

/**
 * uuid = a chosen mobilisation, null = deliberately none (onboarding-
 * only person), undefined = not yet chosen. Parents must block submit
 * while undefined and the session is unscoped.
 */
export type AllocationTarget = string | null | undefined;

export interface AllocationTargetFieldProps {
  value: AllocationTarget;
  onChange: (value: string | null) => void;
}

/**
 * Where newly created or imported people go. When the session is
 * scoped to a mobilisation (header switcher), allocation follows the
 * scope and this renders a notice saying so. In the cross-mobilisation
 * "All" view it renders a required picker instead, so people are never
 * silently created outside every mobilisation.
 */
export function AllocationTargetField({
  value,
  onChange,
}: AllocationTargetFieldProps) {
  const { data: user } = useCurrentUser();
  const list = useMobilisationsList();
  const days = (list.data ?? []).filter((m) => m.kind !== 'onboarding');

  const scopedTo = user?.current_mobilisation_id ?? null;
  if (scopedTo) {
    const name =
      days.find((m) => m.id === scopedTo)?.name ?? 'the current mobilisation';
    return (
      <div className="flex items-start gap-2 rounded-md bg-accent/10 px-3 py-2 text-sm text-accent">
        <CalendarDays className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          New people will be added to <strong>{name}</strong>. Use the
          mobilisation switcher in the top nav to target a different one.
        </span>
      </div>
    );
  }

  const selectValue =
    value === undefined ? '' : value === null ? NO_MOBILISATION : value;
  return (
    <label className="block text-sm">
      <span className="font-medium text-fg-muted">
        Mobilisation <span className="text-danger">*</span>
      </span>
      <select
        required
        value={selectValue}
        onChange={(e) =>
          onChange(e.target.value === NO_MOBILISATION ? null : e.target.value)
        }
        className="mt-1 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
      >
        <option value="" disabled>
          Choose where new people go...
        </option>
        {days.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name} ({formatDate(m.date)}, {m.status})
          </option>
        ))}
        <option value={NO_MOBILISATION}>
          No mobilisation day (onboarding programme only)
        </option>
      </select>
      <p className="mt-1 text-[11px] text-fg-muted">
        You're viewing all mobilisations, so pick where these people belong.
        Membership can be changed later from the mobilisations admin page.
      </p>
    </label>
  );
}
